// src/modules/app-views/services/pages/Document/tabs/AiInfoTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Alert, Grid } from 'antd';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';

// Section Components
// Collapse keys that should start OPEN by default (all others start closed)
export const DEFAULT_OPEN_KEYS = [
  'ai-meta',
  'service-data',
  'cost-analysis',
  'suggested-pricing',
  'fiscal-data',
  'tax-retentions',
  'classification',
  'overview',
  'tools',
  'pre-checklist',
  'steps',
  'common-problems',
  'tips',
  'videos',
  'websites',
  'search-terms',
];

import AiMetaSection from './AiMetaSection';
import ServiceDataSection from './ServiceDataSection';
import CostAnalysisSection from './CostAnalysisSection';
import SuggestedPricingSection from './SuggestedPricingSection';
import FiscalDataSection from './FiscalDataSection';
import TaxRetentionsSection from './TaxRetentionsSection';
import ClassificationSection from './ClassificationSection';
import ProcedureSection from './ProcedureSection';
import ReferencesSection from './ReferencesSection';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * AiInfoTab - Displays AI cataloging information for the service
 */
const AiInfoTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.md;
  const hasAiMeta = data?.aiCatalogMeta?.catalogedByAI === true;
  const aiResponse = data?.aiCatalogMeta?.aiResponse;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasAiMeta) {
    return (
      <div className="container" style={{ padding: isMobile ? 16 : 24 }}>
        <Alert
          type="info"
          showIcon
          title={translateX('service_not_cataloged_by_ai')}
          description={translateX('service_not_cataloged_by_ai_description')}
        />
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="aiInfo" isFetching={loading} data={data} />

      {/* AI CATALOG META SECTION */}
      <AiMetaSection data={data} isMobile={isMobile} />

      {/* SERVICE DATA SECTION */}
      <ServiceDataSection data={data} aiResponse={aiResponse} isMobile={isMobile} />

      {/* COST ANALYSIS SECTION */}
      <CostAnalysisSection data={data} aiResponse={aiResponse} isMobile={isMobile} />

      {/* SUGGESTED PRICING SECTION */}
      <SuggestedPricingSection data={data} aiResponse={aiResponse} isMobile={isMobile} />

      {/* FISCAL DATA SECTION (CNAE, LC116, ISS) */}
      <FiscalDataSection data={data} aiResponse={aiResponse} isMobile={isMobile} />

      {/* TAX RETENTIONS SECTION */}
      <TaxRetentionsSection data={data} aiResponse={aiResponse} isMobile={isMobile} />

      {/* CLASSIFICATION SECTION */}
      <ClassificationSection data={data} aiResponse={aiResponse} isMobile={isMobile} />

      {/* TECHNICAL PROCEDURE SECTION */}
      <ProcedureSection aiResponse={aiResponse} isMobile={isMobile} />

      {/* TECHNICAL REFERENCES SECTION */}
      <ReferencesSection aiResponse={aiResponse} isMobile={isMobile} />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AiInfoTab;
